import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';
import { Form, Formik, FormikProps } from 'formik';

import i18n from '../../utilities/i18n';
import { ROUTES } from '../../constants';
import log from '../../utilities/log';
import mediator from '../../utilities/mediator';
import { AuthenticateResponse } from '../../models';
import {
  Button,
  Card,
  Highlight,
  IconBannersWarning,
  LinkText,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import TextInputWithError from '../ui/forms/TextInputWithError';
import PasswordInputWithError from '../ui/forms/PasswordInputWithError';
import PreventSubmitWrapper from '../ui/forms/PreventSubmitWrapper';
import { AuthCode, MFAChallengeResponse } from 'adc-sirius-module-common';
import { useAppSelector } from '../../reducers/hooks';

export default function LoginForm() {
  const navigate = useNavigate();
  const sessionExpired = useAppSelector((state) => state.auth.sessionExpired);

  const [lockoutUntil, setLockoutUntil] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());

  const lockoutSecondsRemaining = Math.max(
    0,
    Math.ceil((lockoutUntil - currentTime) / 1000),
  );

  const lockoutMinutesRemaining =
    lockoutSecondsRemaining > 0 ? Math.ceil(lockoutSecondsRemaining / 60) : 0;

  useEffect(() => {
    if (lockoutUntil <= Date.now()) {
      return;
    }

    const timer = window.setInterval(() => {
      const now = Date.now();

      setCurrentTime(now);

      if (now >= lockoutUntil) {
        setLockoutUntil(0);
        window.clearInterval(timer);
      }
    }, 60_000);

    return () => window.clearInterval(timer);
  }, [lockoutUntil]);

  const initialValues = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().required(i18n.t('Auth_LoginForm.emailRequired')),
    password: Yup.string().required(i18n.t('Auth_LoginForm.passwordRequired')),
  });

  const onSubmit = (values, { setStatus }) =>
    mediator
      .request<AuthenticateResponse>('auth:authenticate', values)
      .then((result) => {
        setStatus('');

        if (result.loggedIn) {
          navigate(ROUTES.ROOT);
        } else if (result.code === AuthCode.AccountSuspended) {
          setStatus('SecurityRules.23');
        } else if (result.code === AuthCode.PasswordExpired) {
          navigate(ROUTES.AUTH.CHANGE_PASSWORD, {
            state: { email: values.email },
          });
        } else if (result.code === AuthCode.AccountNotVerified) {
          setStatus('Auth_LoginForm.accountNotVerified');
        } else if (result.code === AuthCode.MFARequired) {
          const mfaConfig = result.data as MFAChallengeResponse;

          navigate(ROUTES.AUTH.MULTI_FACTOR_AUTH, {
            state: { ...mfaConfig },
          });
        } else {
          setStatus('Auth_LoginForm.notLoggedIn');
        }
      })
      .catch((err) => {
        log.error(err);

        if (err.data?.code === AuthCode.LockedOut) {
          const retryAfterSeconds = Number(
            err.data?.data?.retryAfterSeconds || 0,
          );
          const lockedUntil = Number(err.data?.data?.lockedUntil || 0) * 1000;
          const deadline = Math.max(
            lockedUntil,
            Date.now() + retryAfterSeconds * 1000,
          );

          setCurrentTime(Date.now());
          setLockoutUntil(deadline);
          setStatus('SecurityRules.60');
        } else {
          setStatus('Auth_LoginForm.unexpectedIssue');
        }
      });

  return (
    <VStack className="align-center">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formikProps: FormikProps<any>) => {
          const status =
            formikProps.status ??
            (sessionExpired ? 'Auth_LoginForm.sessionExpired' : undefined);

          return (
            <Form id="login-form" data-testid="login-form" noValidate>
              <Card>
                <VStack margin="$4" gap="$8">
                  <VStack alignItems="center">
                    <Text type="$h2.strong">
                      {i18n.t('Auth_LoginForm.title')}
                    </Text>
                  </VStack>
                  {status &&
                  !(
                    status === 'SecurityRules.60' &&
                    lockoutMinutesRemaining <= 0
                  ) ? (
                    <VStack marginBottom={'$4'}>
                      <Highlight
                        variant="danger"
                        iconStart={
                          <IconBannersWarning
                            color="$neutral.0"
                            accentColor="$support.danger.default"
                          />
                        }
                        title={i18n.t('Auth_LoginForm.errorTitle')}
                        body={
                          status === 'SecurityRules.60'
                            ? i18n.t(
                                `SecurityRules.60${lockoutMinutesRemaining === 1 ? '_1' : ''}`,
                                { count: lockoutMinutesRemaining },
                              )
                            : i18n.t(status)
                        }
                        testID="status-message"
                      />
                    </VStack>
                  ) : null}
                  <PreventSubmitWrapper>
                    <TextInputWithError
                      name="email"
                      label={i18n.t('Auth_LoginForm.email')}
                      testID="email-input"
                    />

                    <PasswordInputWithError
                      label={i18n.t('Auth_LoginForm.password')}
                      name="password"
                      autocomplete="current-password"
                      testID="password-input"
                    />
                  </PreventSubmitWrapper>

                  <VStack alignItems="center" gap="$2">
                    <Button
                      disabled={
                        formikProps.isSubmitting ||
                        !formikProps.isValid ||
                        lockoutMinutesRemaining > 0
                      }
                      label={i18n.t('Auth_LoginForm.continue')}
                      onPress={formikProps.submitForm}
                      variant="primary"
                      testID="continue-button"
                    />
                    <LinkText
                      onPress={() => navigate(ROUTES.AUTH.FORGOT_PASSWORD)}
                      type="$bodySmall.default"
                      testID="forgot-password-link"
                    >
                      {i18n.t('Auth_LoginForm.forgotPassword')}
                    </LinkText>
                  </VStack>
                </VStack>
              </Card>
            </Form>
          );
        }}
      </Formik>
    </VStack>
  );
}
